import { useState, useMemo } from 'react';
import { Plus } from 'lucide-react';

import {
  TeamAccessViewModel,
  UserAccessViewModel,
} from '@/portainer/models/access';
import { useUsers } from '@/portainer/users/queries';
import { useTeams } from '@/react/portainer/users/teams/queries';
import { useRbacRoles } from '@/react/portainer/users/RolesView/useRbacRoles';

import { FormControl } from '@@/form-components/FormControl';
import { PortainerSelect } from '@@/form-components/PortainerSelect';
import { TextTip } from '@@/Tip/TextTip';
import { Button } from '@@/buttons';
import { FormSection } from '@@/form-components/FormSection';

import { UserTeamSelector } from '../../UserTeamSelector';

import { AccessListDatatable } from './AccessListDatatable';
import { UnifiedAccess, DockerRBACFormData } from './types';

interface DockerRBACConstraintsProps {
  formData: DockerRBACFormData;
  onChange: <K extends keyof DockerRBACFormData>(
    key: K,
    value: DockerRBACFormData[K]
  ) => void;
  isCreating?: boolean;
}

type UserTeamValue = {
  Type: 'user' | 'team';
  Id: number;
  Name: string;
};

export function DockerRBACInnerForm({
  formData,
  onChange,
  isCreating,
}: DockerRBACConstraintsProps) {
  const { userOptions, teamOptions, roleOptions, isRoleOptionsLoading } =
    usePolicyOptions();

  const [tempSelectedRole, setTempSelectedRole] = useState<number>(0);
  const [tempSelectedUsersAndTeams, setTempSelectedUsersAndTeams] = useState<
    UserTeamValue[]
  >([]);

  const accessList = useMemo(
    () => formData.accessList || [],
    [formData.accessList]
  );

  // Combine users and teams into one list with proper structure
  const usersAndTeamsOptions = [
    ...userOptions.map((u) => ({
      label: u.label,
      value: { Type: 'user' as const, Id: u.value, Name: u.label },
    })),
    ...teamOptions.map((t) => ({
      label: t.label,
      value: { Type: 'team' as const, Id: t.value, Name: t.label },
    })),
  ];

  // Get the selected role name
  const selectedRoleName = useMemo(() => {
    const role = roleOptions.find((r) => r.value === tempSelectedRole);
    return role?.label || '';
  }, [tempSelectedRole, roleOptions]);

  // Check if the Add button should be enabled
  const canAddAccess =
    tempSelectedRole > 0 && tempSelectedUsersAndTeams.length > 0;

  return (
    <>
      {/* Access Management */}
      <FormSection title="Add access">
        <FormControl label="Select users and/or teams" required>
          <UserTeamSelector
            options={usersAndTeamsOptions}
            value={tempSelectedUsersAndTeams}
            onChange={setTempSelectedUsersAndTeams}
          />
        </FormControl>
        <FormControl label="Select Role" required>
          <PortainerSelect
            options={roleOptions}
            value={tempSelectedRole}
            onChange={setTempSelectedRole}
            isLoading={isRoleOptionsLoading}
            placeholder="Select a role"
            data-cy="role-select"
          />
        </FormControl>

        <div className="form-group">
          <div className="col-sm-12 flex gap-2">
            <Button
              icon={Plus}
              color="primary"
              size="small"
              disabled={!canAddAccess}
              onClick={handleAddAccess}
              data-cy="add-access-button"
              className="!ml-0"
            >
              Add Access
            </Button>
          </div>
        </div>
      </FormSection>

      {/* Access List Datatable */}
      <div className="-mx-4">
        <AccessListDatatable
          formData={{ accessList }}
          onChange={onChange}
          roleOptions={roleOptions}
        />
      </div>

      {/* Access Info message */}
      <div className="form-group">
        <div className="col-sm-12">
          <TextTip color="blue">
            {(() => {
              if (accessList.length > 0) {
                const userCount = accessList.filter(
                  (a) => a.Type === 'user'
                ).length;
                const teamCount = accessList.filter(
                  (a) => a.Type === 'team'
                ).length;

                return `Access configured for ${userCount} user(s) and ${teamCount} team(s).`;
              }

              return isCreating
                ? 'Please add at least one user or team to create access.'
                : 'Please add at least one user or team to update access.';
            })()}
          </TextTip>
        </div>
      </div>
    </>
  );

  function handleAddAccess() {
    if (!canAddAccess) return;

    let newAccessList = [...accessList];

    newAccessList = tempSelectedUsersAndTeams.reduce((result, selection) => {
      if (selection.Type === 'user') {
        return addOrUpdateUser(result, selection.Id);
      }
      if (selection.Type === 'team') {
        return addOrUpdateTeam(result, selection.Id);
      }

      return result;
    }, newAccessList);

    onChange('accessList', newAccessList);
    setTempSelectedUsersAndTeams([]);
  }

  function addOrUpdateUser(
    accessList: UnifiedAccess[],
    userId: number
  ): UnifiedAccess[] {
    const user = userOptions.find((u) => u.value === userId);
    if (!user) return accessList;

    const userAccess = new UserAccessViewModel({
      Id: userId,
      Username: user.label,
    });
    userAccess.Role = {
      Id: tempSelectedRole,
      Name: selectedRoleName,
    };
    const userEntry: UnifiedAccess = userAccess as UnifiedAccess;

    const existingIndex = accessList.findIndex(
      (a) => a.Type === 'user' && a.Id === userId
    );

    if (existingIndex === -1) {
      return [...accessList, userEntry];
    }

    const beforeEntry = accessList.slice(0, existingIndex);
    const afterEntry = accessList.slice(existingIndex + 1);
    return [...beforeEntry, userEntry, ...afterEntry];
  }

  function addOrUpdateTeam(
    accessList: UnifiedAccess[],
    teamId: number
  ): UnifiedAccess[] {
    const team = teamOptions.find((t) => t.value === teamId);
    if (!team) return accessList;

    const teamAccess = new TeamAccessViewModel({
      Id: teamId,
      Name: team.label,
    });
    teamAccess.Role = {
      Id: tempSelectedRole,
      Name: selectedRoleName,
    };
    const teamEntry: UnifiedAccess = teamAccess as UnifiedAccess;

    const existingIndex = accessList.findIndex(
      (a) => a.Type === 'team' && a.Id === teamId
    );

    if (existingIndex === -1) {
      return [...accessList, teamEntry];
    }

    const beforeEntry = accessList.slice(0, existingIndex);
    const afterEntry = accessList.slice(existingIndex + 1);
    return [...beforeEntry, teamEntry, ...afterEntry];
  }
}

function usePolicyOptions() {
  const usersQuery = useUsers(
    false, // includeAdministrator
    0, // environmentId
    true, // enabled
    (users) =>
      users.map((user) => ({
        value: user.Id,
        label: user.Username,
      }))
  );

  const teamsQuery = useTeams(false, 0, {
    select: (teams) =>
      teams.map((team) => ({
        value: team.Id,
        label: team.Name,
      })),
  });

  const rbacRolesQuery = useRbacRoles({
    select: (roles) =>
      roles
        .filter((role) => role.Scope && (role.Scope.docker || role.Scope.swarm))
        .map((role) => ({
          value: role.Id,
          label: role.Name,
        })),
  });

  return {
    userOptions: usersQuery.data || [],
    teamOptions: teamsQuery.data || [],
    roleOptions: rbacRolesQuery.data || [],
    isUserOptionsLoading: usersQuery.isLoading,
    isTeamOptionsLoading: teamsQuery.isLoading,
    isRoleOptionsLoading: rbacRolesQuery.isLoading,
  };
}
